/**
 * Math Processing Helpers
 *
 * Utilizing the CBL library to process math expressions to IoTS script
 *   CBL library: https://gitlab.allterco.net/Shelly/be/cbl
 */
import { type FreeVariable } from '@Shelly/cbl/src/core/types';
import { transpile } from '@Shelly/cbl/src/lib';

import type { MathParameter } from 'src/types/Scenes/MathConditionUI';
import { ErrorStructure } from '@Shelly/cbl/src/core/errors';

//Supported comparison operators
export const COMPARISON_OPERATORS = ['<', '>', '=', '=>', '<=', '!='];
export const CALCULATION_OPERATORS = ['+', '-', '*', '/'];
export const MAX_PARAMETERS = 10; // Max number of supported parameters

/** Preprocesses Math Expression to CBL-format expression
 * CBL supports programming language-like expressions.
 *
 *	- Replaces '=>' with '>='
 *	- Replaces '<=' with '=='
 * @param expression - Math expression to preprocess
 * @returns Preprocessed expression
 */
export const preProcessMathExpression = (expression: string): string => {
	expression = expression
		.replaceAll('=>', '>=') // >= is the comparison operator in CBL
		.replaceAll('= >', '>='); // >= is the comparison operator in CBL

	return expression;
};

/**
 * Process the math expression to generate the IOTS script using the CBL library
 * 	Note: Using CBL v0.0.4
 *
 * @param expression
 * @param parameters
 * @returns		IoTS script object
 * @throws		{Error} On invalid expression or error during processing
 */
export const processExpression = (expression: string, parameters: Array<MathParameter>): any => {
	if (!validateComparisonOperators(expression)) {
		console.log('[Math Processing] Error: invalid comparison operator in expression');
		throw new Error('NO_COMPARISON_OPERATOR', {
			cause: { COMPARISON_OPERATORS },
		});
	}

	expression = preProcessMathExpression(expression);
	// console.log('[Math Processing] expression:', expression);

	const variables: FreeVariable[] = prepareParameters(parameters);

	// CBL Library transpilation - Prepare the expression for IoTS script
	const jembResult = transpile(expression, variables);

	if (isCBLError(jembResult)) {
		//Handle error
		let err = jembResult as ErrorStructure;
		if (import.meta.env.DEV) {
			console.warn('[Math Processing] CBL Error:', err.errorString, err.message, err.data);
			console.warn('===> CBL ERROR EXPRESSION: ', expression);
		}

		throw new Error(err.errorString, {
			cause: {
				data: jembResult.data,
				message: jembResult.message,
				expression,
				COMPARISON_OPERATORS,
				CALCULATION_OPERATORS,
			},
		});
	}

	if (import.meta.env.DEV) {
		console.log('[Math Processing] CBL Result:');
		console.log('===> CODE: ', expression);
		console.log('===> VARIABLES: ', JSON.stringify(variables, null, 2));
		console.log('===> LIB TRANSPILE: ', JSON.stringify(jembResult, null, 2));
	}

	return jembResult;
};

/**
 * Check if the object is CBL Library Error
 *
 * @param obj - Object to check
 * @returns		true the result is a CBL error
 */
const isCBLError = (obj: any): boolean => {
	// Check if the object is an instance of the CBL Error
	if (!obj || obj instanceof ErrorStructure || obj.hasOwnProperty('errorString')) {
		return true;
	}

	return false;
};

/**
 * Prepares the parameters for the CBL library
 * 	- Converts MathParameter objects to FreeVariable objects
 * 	- Validates parameters are not empty
 * 	- Returns an array of FreeVariable objects for the CBL library
 *
 * @param {MathParameter[]} parameters  - Array of MathParameter objects
 * @returns {FreeVariable[]} - Array of FreeVariable objects
 */
const prepareParameters = (parameters: Array<MathParameter>): Array<FreeVariable> => {
	// Example library usage:
	// const variables: FreeVariable[] = [
	// 	// { cblName: 'x', jembName: 'dev:134:r1', type: 'anych' }
	// ];

	return parameters.map((param) => {
		if (!param.name || !param.value) {
			console.log('[Math Processing] Skipping Invalid param:', param);
			return;
		}
		return {
			cblName: param.name,
			jembName: param.value,
			type: 'anych',
		};
	});
};

/**
 * Validate the comparison operators in the expression
 *
 * @param expression
 * @returns	true if the expression contains a valid comparison operator
 */
export const validateComparisonOperators = (expression: string): boolean => {
	return [...COMPARISON_OPERATORS].some((op) => expression.includes(op));
};
